/**
 * Profile Service
 * Handles user profile-related API operations including profile management,
 * fitness data, and profile-specific validations using the CRUD service as base.
 */

import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';

import type { ID } from '../types/common/id.types';
import type { UserProfile } from '../types/domain/profile.types';

// =============================================================================
// PROFILE SERVICE CLASS
// =============================================================================

export class ProfileService extends CrudService<UserProfile> {
  constructor() {
    super({
      baseUrl: '/profile',
      enableCaching: true,
      cacheTTL: 15 * 60 * 1000, // 15 minutes (longer than user service)
      cacheVariesByUnit: true, // profile goals/achievement PR values arrive in the user's unit
    });
  }

  /**
   * Get current user's full profile
   */
  public async getProfile(id: ID): Promise<UserProfile> {
    try {
      if (!id) {
        throw new Error('Profile username is required');
      }

      const profile = await this.getById(id);
      return this.transformResponse(profile);
    } catch (error) {
      console.error(`Failed to fetch user profile ${id}:`, error);
      rethrowTyped(
        error,
        `Unable to fetch user profile. Please check the profile username and try again.`
      );
    }
  }

  // ---------------------------------------------------------------------------
  // PROFILE VALIDATION AND UTILITIES
  // ---------------------------------------------------------------------------

  /**
   * Check if user profile is complete
   */
  public isProfileComplete(profile: UserProfile): boolean {
    const requiredFields: (keyof UserProfile)[] = [
      'email',
      'username',
      'joined_on',
    ];

    return requiredFields.every(
      (field) =>
        profile[field] !== undefined &&
        profile[field] !== null &&
        profile[field] !== ''
    );
  }

  /**
   * Get user's display name with fallback logic
   */
  public getDisplayName(profile: UserProfile): string {
    return profile.username.trim();
  }

  // ---------------------------------------------------------------------------
  // OVERRIDE METHODS FROM CRUD SERVICE
  // ---------------------------------------------------------------------------

  /**
   * Transform profile data received from API
   */
  protected override transformResponse(data: UserProfile): UserProfile {
    // Ensure consistent data format
    return {
      ...data,
    };
  }

  // ---------------------------------------------------------------------------
  // CACHE MANAGEMENT
  // ---------------------------------------------------------------------------

  /**
   * Clear all profile-related cache.
   *
   * Everything this service caches is a profile, so this is the whole cache.
   * (It used to delete a `'current-profile'` key that nothing ever wrote.)
   */
  public clearProfileCache(): void {
    this.clearCache();
  }

  /**
   * Refresh profile cache
   */
  public async refreshProfileCache(id: ID): Promise<UserProfile> {
    this.clearProfileCache();
    return await this.getProfile(id);
  }
}

// =============================================================================
// SERVICE INSTANCE AND FACTORY
// =============================================================================

export const profileService = new ProfileService();

/**
 * Create a new profile service instance with custom configuration
 */
export const createProfileService = (): ProfileService => {
  return new ProfileService();
};
